import trajectory from './trajectory.ts'
import { standardDeviation } from './stats.js'

export function processTrk (contents, pressure, temperature, weight, caliber, minSnr = 15, startRange = 0, endRange = 40) {
  minSnr = minSnr || 15

  let data
  if (typeof contents === 'string') {
    data = contents
      .split('\n')
      .map(v => v.split(';'))
      .map(v => v.map(parseFloat))
      .filter(v => parseFloat(v[2]) >= 0)
      .map(v => ({
        vel: parseFloat(v[1]),
        dist: parseFloat(v[2]),
        snr: parseFloat(v[3]),
        include: 0
      }))
  } else {
    data = contents
  }

  const snrs = data.map(v => v.snr).sort((a, b) => b - a)
  const snr2nd = snrs[1]
  if (snr2nd < minSnr) minSnr = 0
  let vdata = data.filter(v => v.snr >= minSnr && v.dist > 0)
  if (startRange) {
    vdata = vdata.filter(v => v.dist >= startRange)
  }
  if (endRange && endRange > startRange) {
    vdata = vdata.filter(v => v.dist <= endRange)
  }
  vdata.forEach(v => { v.include = 20 })
  // TODO this should use linearRegression from lib/stats.js
  const sumx = vdata.map(v => parseFloat(v.dist)).reduce((a, b) => a + b, 0)
  const sumy = vdata.map(v => parseFloat(v.vel)).reduce((a, b) => a + b, 0)
  const sumxx = vdata.map(v => parseFloat(v.dist) * parseFloat(v.dist)).reduce((a, b) => a + b, 0)
  const sumxy = vdata.map(v => parseFloat(v.dist) * parseFloat(v.vel)).reduce((a, b) => a + b, 0)
  const sumyy = vdata.map(v => parseFloat(v.vel) * parseFloat(v.vel)).reduce((a, b) => a + b, 0)
  const slope = (vdata.length * sumxy - sumx * sumy) / (vdata.length * sumxx - sumx * sumx)
  const rSquared = Math.pow((vdata.length * sumxy - sumx * sumy) / Math.sqrt((vdata.length * sumxx - sumx * sumx) * (vdata.length * sumyy - sumy * sumy)), 2)
  const intercept = (sumy - slope * sumx) / vdata.length
  const v0 = intercept
  const v0data = data.filter(v => v.dist === 0)
  const lv0 = v0data.length ? v0data[0].vel : v0
  const v100 = v0 + slope * 100

  // http://cbe.udel.edu/wp-content/uploads/2019/03/FittingData.pdf
  const sSum = vdata.reduce((sum, v) => {
    return Math.pow(v.vel - slope * v.dist - intercept, 2) + sum
  }, 0)
  const s = Math.sqrt(sSum / (vdata.length - 2))
  const es1 = vdata.reduce((sum, v) => Math.pow(v.dist, 2) + sum, 0)
  const es2 = vdata.reduce((sum, v) => v.dist + sum, 0)
  const errorPercent = Math.abs(s * Math.sqrt(vdata.length / (vdata.length * es1 - Math.pow(es2, 2))) / slope)

  let bc
  if (pressure && temperature && v0 > v100) {
    bc = estimateBC(v0, v100, 100, pressure, temperature)
  }

  let form_factor
  if (bc && weight && caliber) {
    const sectional_density = weight / 7000 / Math.pow(caliber, 2);
    form_factor = sectional_density / bc
  }

  return {
    ff: form_factor,
    data,
    weight,
    caliber,
    errorPercent,
    rSquared,
    lv0,
    v0,
    v100,
    bc
  }
}

export function estimateBC (v0, vr, range, pressure, temperature) {
  let maxBc = 3
  let minBc = .01
  let bc

  for (let i = 0; i < 1000; ++i) {
    bc = (maxBc + minBc) / 2
    const t = trajectory({
      bc,
      v0,
      range,
      temperature,
      pressure
    })
    const vcr = Math.round(t[range].velocity)
    if (vcr > vr) {
      maxBc = bc
    } else {
      minBc = bc
    }
    if (maxBc - minBc <= .0005) break
  }
  return parseFloat(bc.toFixed(3))
}

export function trackStats (tracks) {
  tracks = tracks.filter(shot => !shot.filtered)
  const stats = {}
  const bcs = tracks.map(v => v.bc).filter(v => v)
  if (bcs.length) {
    const bcSum = bcs.reduce((a, b) => a + b, 0)
    const bcAvg = bcSum / bcs.length
    const bcSd = ((standardDeviation(bcs) / bcAvg) * 100).toFixed(1) + '%'
    const bcMax = Math.max.apply(Math, bcs)
    const bcMin = Math.min.apply(Math, bcs)
    const bcEs = (((bcMax - bcMin) / bcAvg) * 100).toFixed(1) + '%'
    stats.bcAvg = bcAvg
    stats.bcSd = bcSd
    stats.bcEs = bcEs
  }
  const ffs = tracks.map(v => v.ff).filter(v => v)
  if (ffs.length) {
    const ffSum = ffs.reduce((a, b) => a + b, 0)
    const ffAvg = ffSum / ffs.length
    stats.ffAvg = ffAvg
  }
  const errs = tracks.map(v => v.errorPercent).filter(v => v)
  if (errs.length) {
    const errSum = errs.reduce((a, b) => a + b, 0)
    const errAvg = errSum / errs.length
    stats.errAvg = errAvg
  }
  const r2s = tracks.map(v => v.rSquared).filter(v => v)
  if (r2s.length) {
    const r2Sum = r2s.reduce((a, b) => a + b, 0)
    const r2Avg = r2Sum / r2s.length
    stats.r2Avg = r2Avg
  }
  let vMin = Math.min()
  let vMax = Math.max()
  tracks.forEach(track => {
    track.data.forEach(v => {
      vMin = Math.min(vMin, v.vel)
      vMax = Math.max(vMax, v.vel)
    })
  })
  stats.vMin = vMin
  stats.vMax = vMax
  const v0s = tracks.map(v => v.v0).filter(v => v)
  if (v0s.length) {
    const v0Sum = v0s.reduce((a, b) => a + b, 0)
    const v0Avg = v0Sum / v0s.length
    const v0Sd = standardDeviation(v0s)
    const v0Max = Math.max.apply(Math, v0s)
    const v0Min = Math.min.apply(Math, v0s)
    const v0Es = v0Max - v0Min
    stats.v0Avg = v0Avg
    stats.v0Sd = v0Sd
    stats.v0Es = v0Es
  }
  return stats
}
